import { useCallback, useState } from 'react'
import type { ChangeEvent } from 'react'
import { useFormikContext } from 'formik'
import type { CustomFieldFormModel } from '../../../typings'
import { useAppDispatch } from '@user/store/hooks'
import { isOptionInUse } from '@user/store/customFields'
import { produce } from 'immer'
import { DropResult } from '@user/utils/Draggable'

type State = {
  newOption: string
  inUseCount: number
  optionIndex?: number
}

export function useListActions() {
  const dispatch = useAppDispatch()

  const {
    setValues,
    values: { id: fieldId, options },
    errors: { options: optionErrors },
  } = useFormikContext<CustomFieldFormModel>()

  const caption = `${25 - options.length} out of 25 available`

  const noOptionsError =
    typeof optionErrors === 'string' ? optionErrors : undefined

  const [state, setState] = useState<State>(() => ({
    newOption: '',
    inUseCount: 0,
  }))

  const { inUseCount, newOption, optionIndex } = state

  const addDisabled = newOption.length === 0 || options.length === 25

  const onChangeNewOption = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setState((state) => ({ ...state, newOption: event.target.value })),
    [],
  )

  const addNewOption = useCallback(() => {
    setState((state) =>
      produce(state, (draft) => {
        draft.newOption = ''
      }),
    )

    setValues((values) =>
      produce(values, (draft) => {
        draft.options.push({
          id: '',
          active: true,
          name: newOption,
        })
      }),
    )
  }, [newOption, setValues])

  const onDragEnd = useCallback(
    (result: DropResult) => {
      setValues((v) => {
        const options = [...v.options]
        const dropped = v.options[result.sourceIndex]

        options.splice(result.sourceIndex, 1)
        options.splice(result.destinationIndex, 0, dropped)
        return { ...v, options }
      })
    },
    [setValues],
  )

  const onRemoveOption = useCallback(
    async (index: number) => {
      const { id } = options[index] ?? undefined

      if (!!fieldId && !!id) {
        const response = await dispatch(
          isOptionInUse({
            id: fieldId,
            optionId: id,
          }),
        )

        if (isOptionInUse.fulfilled.match(response)) {
          const count = response.payload

          setState((state) =>
            produce(state, (draft) => {
              draft.inUseCount = count
              draft.optionIndex = count === 0 ? undefined : index
            }),
          )

          if (count === 0) {
            // not in use, remove it
            setValues((values) =>
              produce(values, (draft) => {
                draft.options.splice(index, 1)
              }),
            )
          }
        }
      } else {
        setState((state) =>
          produce(state, (draft) => {
            draft.inUseCount = 0
            draft.optionIndex = undefined
          }),
        )
        setValues((values) =>
          produce(values, (draft) => {
            draft.options.splice(index, 1)
          }),
        )
      }
    },
    [dispatch, fieldId, options, setValues],
  )

  const onDelete = useCallback(() => {
    if (optionIndex !== undefined) {
      setValues((values) =>
        produce(values, (draft) => {
          draft.options.splice(optionIndex, 1)
        }),
      )
    }

    setState((state) =>
      produce(state, (draft) => {
        draft.optionIndex = undefined
      }),
    )
  }, [optionIndex, setValues])

  const onDeactivate = useCallback(() => {
    if (optionIndex !== undefined) {
      setValues((values) =>
        produce(values, (draft) => {
          draft.options[optionIndex].active = false
        }),
      )
    }

    setState((state) =>
      produce(state, (draft) => {
        draft.optionIndex = undefined
      }),
    )
  }, [optionIndex, setValues])

  const onClose = useCallback(() => {
    setState((state) =>
      produce(state, (draft) => {
        draft.optionIndex = undefined
      }),
    )
  }, [])

  return {
    caption,
    options,
    onDragEnd,
    onRemoveOption,

    addOption: {
      newOption,
      addDisabled,
      addNewOption,
      noOptionsError,
      onChangeNewOption,
    },

    deleteDialog: {
      onClose,
      onDelete,
      inUseCount,
      optionIndex,
      onDeactivate,
    },
  }
}
